import { Modal, Spin, Typography } from "antd";
import { useEffect, useState } from "react";
import { previewTemplate } from "../repository/data.repository";
import { TemplatePreview, TemplatePreviewInput } from "../repository/data.default";
import Editor from "../controls/Editor";
import { getProblemMessage } from "../utils/http";

export interface TemplatePreviewModalProps {
  readonly open?: boolean;
  readonly input?: TemplatePreviewInput;
  readonly onClose?: () => void;
}

/**
 * Renders a (possibly unsaved) template with sample variable values via the backend preview endpoint
 * — so the admin sees the final, sanitized + token-resolved output before saving/sending. The Body is
 * shown through the read-only Quill editor (server already sanitized; this re-sanitizes for display).
 */
function TemplatePreviewModal(props: TemplatePreviewModalProps) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!props.open || !props.input) {
      setPreview(null);
      setError(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    previewTemplate(props.input)
      .then((p) => {
        if (active) {
          setPreview(p);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (active) {
          setError(getProblemMessage(e, "Preview failed"));
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [props.open, props.input]);

  return (
    <Modal
      open={props.open}
      onCancel={props.onClose}
      footer={null}
      title="Template preview"
      width={680}
    >
      {loading && (
        <div className="flex justify-center p-6">
          <Spin />
        </div>
      )}
      {error && <Typography.Text type="danger">{error}</Typography.Text>}
      {!loading && !error && preview && (
        <div className="flex flex-col gap-3">
          <div>
            <Typography.Text type="secondary" className="text-xs">
              Subject
            </Typography.Text>
            <Typography.Paragraph className="m-0 font-medium">
              {preview.subject || <em>(empty)</em>}
            </Typography.Paragraph>
          </div>
          <div>
            <Typography.Text type="secondary" className="text-xs">
              Body (rendered with sample values)
            </Typography.Text>
            <Editor inline readOnly value={preview.body} />
          </div>
        </div>
      )}
    </Modal>
  );
}

export default TemplatePreviewModal;
